// Additional Terms - Maintenance Responsibility Matrix
// This file contains the list of maintenance items and validation logic

export const additionalTermsItems = [
  { id: 1, text: "Property taxes (or increases over a base year/expense stop)" },
  { id: 2, text: "Property insurance (building insurance; sometimes increases only)" },
  { id: 3, text: "Common Area Maintenance (CAM), which often includes:" },
  { id: 4, text: "Parking/drive aisle and truck court maintenance" },
  { id: 5, text: "Asphalt/concrete repairs (patching, striping), sweeping, porter services" },
  { id: 6, text: "Landscaping and irrigation" },
  { id: 7, text: "Exterior lighting and common-area electricity" },
  { id: 8, text: "Trash service for common areas / dumpster service (if shared)" },
  { id: 9, text: "Security (gates, patrols, cameras—if provided)" },
  { id: 10, text: "Stormwater/drainage system maintenance" },
  { id: 11, text: "Pest control (common areas)" },
  { id: 12, text: "Signage maintenance (monument/shared signs)" },
  { id: 13, text: "Snow/ice removal (where applicable)" },
  { id: 14, text: "On-site management and/or CAM admin/management fee" },
  { id: 15, text: "Utilities (often separately metered and paid directly by tenant; if not, allocated back):" },
  { id: 16, text: "Electric, gas, water/sewer, trash (when not separately metered)" },
  { id: 17, text: "Janitorial (sometimes tenant pays directly for their premises; common-area janitorial may be in CAM)" },
  { id: 18, text: "HVAC maintenance/repairs serving the premises (common to push to tenant in industrial)" },
  { id: 19, text: "Fire/life safety monitoring or testing for the premises (sprinkler, alarm panels—varies)" },
  { id: 20, text: "License/permit fees tied to building operations (sometimes included in operating expenses)" },
  { id: 21, text: "Less common, but you'll see it negotiated:" },
  { id: 22, text: "", hasInput: true },
  { id: 23, text: "", hasInput: true }
];

/**
 * Initialize additional terms with default values
 * @returns {Array} Array of term objects with default checkbox values (only id, na, landlord, tenant, and customText for inputs)
 */
export const initializeAdditionalTerms = () => {
  return additionalTermsItems.map(item => {
    const termData = {
      id: item.id,
      na: false,
      landlord: false,
      tenant: false
    };
    
    // Add customText only for items with input fields (items 22 and 23)
    if (item.hasInput) {
      termData.customText = "";
    }
    
    return termData;
  });
};

/**
 * Validate that exactly one option is selected for each term
 * @param {Array} terms - Array of term objects
 * @returns {Object} { isValid: boolean, message: string }
 */
export const validateAdditionalTerms = (terms) => {
  if (!terms || terms.length === 0) {
    return {
      isValid: false,
      message: "Please complete the Additional Terms section"
    };
  }

  // Check if any term has no option selected
  const termsWithNoSelection = terms.filter(
    term => !term.na && !term.landlord && !term.tenant
  );

  if (termsWithNoSelection.length > 0) {
    return {
      isValid: false,
      message: "Please select exactly one option (N/A, Landlord, or Tenant) for each Additional Term item"
    };
  }

  // Check if any term has multiple options selected (shouldn't happen with radio buttons, but validate anyway)
  const termsWithMultipleSelections = terms.filter(term => {
    const selectedCount = [term.na, term.landlord, term.tenant].filter(Boolean).length;
    return selectedCount > 1;
  });

  if (termsWithMultipleSelections.length > 0) {
    return {
      isValid: false,
      message: "Please select only one option (N/A, Landlord, or Tenant) for each Additional Term item"
    };
  }

  return {
    isValid: true,
    message: ""
  };
};

/**
 * Get the display number for a term
 * @param {number} index - Index in the array (1-based)
 * @returns {string} Display number (e.g., "(1)", "(2)", etc.)
 */
export const getTermDisplayNumber = (index) => {
  return `(${index + 1})`;
};

/**
 * Render checkbox symbol
 * @param {boolean} isChecked - Whether the checkbox is checked
 * @returns {string} Checkbox symbol
 */
export const renderCheckbox = (isChecked) => {
  return isChecked ? "☑" : "☐";
};

/**
 * Get display label for a term (handles custom text for input fields)
 * @param {Object} term - Term object from state
 * @param {number} termId - Term ID to look up the static text
 * @returns {string} Display label
 */
export const getTermDisplayLabel = (term, termId) => {
  // Find the static text from additionalTermsItems
  const staticItem = additionalTermsItems.find(item => item.id === termId);
  const staticText = staticItem ? staticItem.text : '';
  
  // If this is an input field and has custom text, return the custom text
  if (term.customText && term.customText.trim() !== '') {
    return term.customText;
  }
  
  // Otherwise return the static text
  return staticText;
};
